import React from "react";
import { m, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { useMarketingMotion } from "./MarketingMotion";

/*
 * Scroll-linked motion primitives for the MARKETING surface only (2026-08-20).
 *
 * These are the site's first framer `useScroll` consumers — everything before this rode
 * whileInView entrances or HeroScrollReveal's hand-rolled scroll listener. The contract every
 * primitive here honours, matching the rest of the marketing motion system:
 *   - `m.*` components only (LazyMotion `strict` boundary in MarketingMotion — `motion.*` throws);
 *   - motion is DOUBLE-gated: framer's `useReducedMotion()` (OS setting, also forced by
 *     MotionConfig when the footer Pause control is on) AND `useMarketingMotion().paused`
 *     directly, because scroll-linked MotionValues bypass MotionConfig's reducedMotion
 *     transition-zeroing — a scrubbed transform must be *removed*, not shortened;
 *   - decorative-only: everything renders aria-hidden and none of it carries content, so the
 *     prerender snapshots, axe gates and SEO body assertions see identical text either way;
 *   - transform/opacity only (compositor-friendly), no layout properties, no loops.
 */

/**
 * Fixed page-top scroll progress bar — the classic "how far through this page am I" affordance.
 * Renders nothing under reduced motion or the marketing Pause switch (it is decoration, not the
 * scroll affordance itself — the scrollbar remains the accessible position indicator).
 */
export function ScrollProgressBar({ className = "" }) {
  const reduce = useReducedMotion();
  const { paused } = useMarketingMotion();
  // Hooks stay unconditional (rules of hooks); the gate only decides the render.
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  if (reduce || paused) return null;
  return (
    <m.div
      aria-hidden="true"
      style={{ scaleX }}
      className={
        "pointer-events-none fixed inset-x-0 top-0 z-[70] h-[2.5px] origin-left " +
        "bg-gradient-to-r from-primary via-sky to-primary " + className
      }
    />
  );
}

/**
 * Gentle scroll-linked parallax for DECORATIVE layers (backdrop orbs, grid washes).
 * Tracks its own element through the viewport and drifts it ±distance px. Under reduced
 * motion / pause it renders the same element with no transform — layout is identical, so the
 * visual-regression gate (which runs reduced) sees today's pixels.
 */
export function Parallax({ children, distance = 28, className = "", style, ...rest }) {
  const reduce = useReducedMotion();
  const { paused } = useMarketingMotion();
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [distance, -distance]);
  const off = reduce || paused;
  return (
    <m.div ref={ref} aria-hidden="true" className={className} style={off ? style : { ...style, y }} {...rest}>
      {children}
    </m.div>
  );
}
